import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { cn } from "~/lib/utils";

// A panel that slides in from the right edge of the viewport, built on the same
// Radix Dialog primitive as ~/components/ui/dialog.tsx (focus trap, ESC/overlay-click
// to close, and ARIA semantics come for free) — only the positioning/animation differ.
const Drawer = DialogPrimitive.Root;
const DrawerTrigger = DialogPrimitive.Trigger;
const DrawerPortal = DialogPrimitive.Portal;
const DrawerClose = DialogPrimitive.Close;

const DrawerOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-50 bg-black/40 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className,
    )}
    {...props}
  />
));
DrawerOverlay.displayName = DialogPrimitive.Overlay.displayName;

const DrawerContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>
>(({ className, children, ...props }, ref) => (
  <DrawerPortal>
    <DrawerOverlay />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        // Mobile: bottom sheet at Figma's fixed 568px (capped to the viewport on short
        // screens), rounded top corners (Figma radius-xl, 16px). Desktop: inset panel,
        // rounded left corners. Height is bounded (not a fixed max-height) since Figma's
        // desktop states range 840-1038px.
        "fixed inset-0 z-50 flex w-full flex-col rounded-none bg-white shadow-lg duration-200",
        "max-sm:top-auto max-sm:h-[568px] max-sm:max-h-dvh max-sm:rounded-t-2xl",
        // Mobile slides up from the bottom (bottom-sheet style); desktop slides in from
        // the right. Each direction is scoped to its own breakpoint so the two never
        // combine into a diagonal slide. (Unsized slide-* utilities only move 0.25rem —
        // mobile uses -full for a true slide-up; desktop keeps its original nudge.)
        "data-[state=open]:animate-in data-[state=closed]:animate-out",
        "max-sm:data-[state=closed]:slide-out-to-bottom-full max-sm:data-[state=open]:slide-in-from-bottom-full",
        "sm:data-[state=closed]:slide-out-to-right sm:data-[state=open]:slide-in-from-right",
        "sm:top-8 sm:bottom-8 sm:left-auto sm:right-0 sm:max-w-[448px] sm:rounded-tl-2xl sm:rounded-bl-2xl",
        className,
      )}
      {...props}
    >
      {children}
    </DialogPrimitive.Content>
  </DrawerPortal>
));
DrawerContent.displayName = DialogPrimitive.Content.displayName;

export { Drawer, DrawerTrigger, DrawerPortal, DrawerClose, DrawerOverlay, DrawerContent };
